import * as L from 'leaflet';
import { ringAreaSqMeters, ringsOf } from '../../../map/features/territories/boundary-snapping';
import { dropRepeats } from './boundary-simplify';

/**
 * What the page refuses to stage, said while the shape is still on screen.
 *
 * THE SERVER IS THE ENFORCEMENT, not this. Every rule below already lives in
 * TerritoryDraftLogic.ParseBoundary, which runs on both the preview and the
 * commit, plus a CHECK constraint on the table
 * (`GeometryType(boundary) IN ('POLYGON','MULTIPOLYGON')`). Nothing here is a
 * rule of its own, and nothing is enforced only here - a rule that lived only on
 * the client would be a rule an API caller does not have.
 *
 * What this is for is the 400 ms and the round trip. A draft preview is
 * debounced and then resolved server-side, so a ring that crosses itself comes
 * back as a red line under a territory NAME, some time after the gesture that
 * caused it and phrased for somebody reading a diff. Said at the moment of the
 * drag, in terms of the outline, it is a correction instead of a rejection.
 *
 * The mapping, so the two can be checked against each other:
 *
 *   server                                        here
 *   GeoJsonReader throws                          "not readable"
 *   not Polygon / MultiPolygon                    "has to be a polygon"
 *   geometry.IsEmpty                              "needs at least three corners"
 *   !geometry.IsValid (self-intersection)         "crosses itself"
 *   !geometry.IsValid (zero area / collinear)     "encloses no area"
 *
 * Deliberately NOT here: overlap with a neighbouring patch. The server repairs
 * that and discloses what it trimmed, which is the whole design of the draft
 * preview; refusing it on the client would contradict a decision that has
 * already been made.
 */

/** Below this a ring has no inside worth owning - a collinear trace lands here,
 *  and so does a shape drawn by accident on one spot. Not zero, because a
 *  degenerate ring computes to a floating-point crumb rather than to 0. */
const MinimumAreaSqMeters = 1;

/**
 * The reason these rings cannot be saved, or null when they can.
 *
 * One message, not a list: the first thing wrong with an outline is the thing to
 * fix, and a stack of complaints about one shape reads as the page arguing.
 */
export function validateRings(rings: readonly (readonly L.LatLng[])[]): string | null {
  if (!rings.length) return 'That outline has no corners yet.';

  for (const raw of rings) {
    const ring = dropRepeats(raw);
    if (ring.length < 3) {
      return 'That outline needs at least three separate corners before it encloses anything.';
    }
    // Before the crossing test, and this order is load-bearing. A ring whose
    // corners are all on one line does double back along itself, so the crossing
    // test would catch it and answer "untangle this" about a shape with nothing
    // to untangle. It is flat, and that is the useful thing to say.
    if (allCollinear(ring)) {
      return 'That outline encloses no area — its corners are all on one line.';
    }
    if (selfIntersects(ring)) {
      // What is wrong and why it matters. What to DO about it is the caller's to
      // add: untangling a corner is advice for somebody holding handles, and the
      // freehand tool has none until the ring is accepted.
      return 'That outline crosses itself — a boundary that overlaps itself has no '
           + 'reliable inside, so leads could not be placed in it.';
    }
    // The backstop, for a ring that is neither flat nor tangled and still bounds
    // nothing measurable - corners a fraction of a metre apart, say.
    if (ringAreaSqMeters(ring) < MinimumAreaSqMeters) {
      return 'That outline encloses no area.';
    }
  }
  return null;
}

/**
 * The same, for a boundary already written as GeoJSON.
 *
 * The structural half is checked against the parsed JSON rather than against
 * ringsOf(), because ringsOf deliberately forgives - it drops the repeated
 * closing position and discards anything it cannot read, so by the time it has
 * finished an unclosed ring and a closed one look identical.
 */
export function validateBoundaryGeoJson(boundaryGeoJson: string): string | null {
  let parsed: { type?: string; coordinates?: unknown };
  try {
    parsed = JSON.parse(boundaryGeoJson) as { type?: string; coordinates?: unknown };
  } catch {
    return 'That boundary is not readable.';
  }

  if (parsed?.type !== 'Polygon' && parsed?.type !== 'MultiPolygon') {
    return 'A territory boundary has to be a polygon.';
  }

  const polygons: unknown[] = parsed.type === 'Polygon'
    ? [parsed.coordinates]
    : (parsed.coordinates as unknown[]) ?? [];

  for (const polygon of polygons) {
    if (!Array.isArray(polygon) || !polygon.length) return 'That boundary is empty.';
    for (const ring of polygon as unknown[]) {
      if (!Array.isArray(ring) || ring.length < 4) {
        return 'That outline needs at least three separate corners before it encloses anything.';
      }
      const first = ring[0] as number[];
      const last = ring[ring.length - 1] as number[];
      if (!Array.isArray(first) || !Array.isArray(last)
        || first[0] !== last[0] || first[1] !== last[1]) {
        return 'That outline is not closed — its last corner has to be its first.';
      }
    }
  }

  return validateRings(ringsOf(boundaryGeoJson));
}

/** True when no corner leaves the line through the first two - a trace drawn
 *  along one street, or a shape collapsed by too coarse a simplification. */
function allCollinear(ring: readonly L.LatLng[]): boolean {
  return ring.every((_, i) => i < 2 || orientation(ring[0], ring[1], ring[i]) === 0);
}

/**
 * True when any two edges of the ring meet anywhere but at a corner they share.
 *
 * O(n²) over a ring the simplifier has already cut to tens of corners, which is
 * nothing, and it is run once per gesture rather than per pointer move.
 *
 * Measured in degrees. The crossing question is topological and the lat/lng grid
 * is monotone in both axes, so two edges cross on the ground exactly when they
 * cross here; the distortion that makes degrees the wrong unit for a DISTANCE
 * does not reach this.
 *
 * Touching counts, not only crossing. A ring pinched into a figure of eight
 * meets itself at one point without any edge passing through another, and PostGIS
 * calls that invalid for the same reason it calls a crossing invalid: there is no
 * single inside left to test a lead against.
 */
function selfIntersects(ring: readonly L.LatLng[]): boolean {
  const n = ring.length;
  for (let i = 0; i < n; i++) {
    const a1 = ring[i];
    const a2 = ring[(i + 1) % n];
    for (let j = i + 1; j < n; j++) {
      // Edges that share a corner meet there by construction. j === i + 1 is the
      // next edge along; i === 0 with j === n - 1 is the closing edge, which is
      // the previous one for the first.
      if (j === i + 1 || (i === 0 && j === n - 1)) continue;
      if (segmentsMeet(a1, a2, ring[j], ring[(j + 1) % n])) return true;
    }
  }
  return false;
}

function segmentsMeet(p1: L.LatLng, p2: L.LatLng, p3: L.LatLng, p4: L.LatLng): boolean {
  const d1 = orientation(p3, p4, p1);
  const d2 = orientation(p3, p4, p2);
  const d3 = orientation(p1, p2, p3);
  const d4 = orientation(p1, p2, p4);

  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0))
   && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) return true;

  // Collinear, or an endpoint sitting on the other edge.
  if (d1 === 0 && between(p3, p1, p4)) return true;
  if (d2 === 0 && between(p3, p2, p4)) return true;
  if (d3 === 0 && between(p1, p3, p2)) return true;
  if (d4 === 0 && between(p1, p4, p2)) return true;
  return false;
}

/** Sign of the cross product: which side of a-b the point c falls on. */
function orientation(a: L.LatLng, b: L.LatLng, c: L.LatLng): number {
  const value = (b.lng - a.lng) * (c.lat - a.lat) - (b.lat - a.lat) * (c.lng - a.lng);
  return value > 0 ? 1 : value < 0 ? -1 : 0;
}

/** `q` inside the bounding box of `p`-`r`, used only once they are collinear. */
function between(p: L.LatLng, q: L.LatLng, r: L.LatLng): boolean {
  return Math.min(p.lng, r.lng) <= q.lng && q.lng <= Math.max(p.lng, r.lng)
      && Math.min(p.lat, r.lat) <= q.lat && q.lat <= Math.max(p.lat, r.lat);
}
